import { useState } from "react";
import { toast } from "sonner";
import { Eye, Check, X, Upload, AlertTriangle, Sparkles, Lock } from "lucide-react";
import { useApi, useFetch, errMsg, inr } from "../lib/api";
import { useMember } from "../lib/member";
import { Pill } from "./shared";
import { Button } from "./ui/button";
import { ReasonDialog } from "./ReasonDialog";
import { useScoreGate } from "./ScoreGate";
import { FormDialog } from "./FormDialog";
import { DatePicker } from "./DateField";

const viewCopy = async (api, id) => {
  try { const r = await api.get(`/clra-submissions/${id}/document`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
  catch { toast.error("Copy not available"); }
};
const TONE = { PENDING: "amber", APPROVED: "green", REJECTED: "red" };
const MAX_MB = 20;

const SubmitForm = ({ onDone }) => {
  const api = useApi();
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [reading, setReading] = useState(false);
  const pick = async (file) => {
    setF((x) => ({ ...x, file })); setAi(null);
    if (!file || !["application/pdf", "image/png", "image/jpeg"].includes(file.type) || file.size > MAX_MB * 1024 * 1024) return;
    const fd = new FormData(); fd.append("file", file);
    setReading(true);
    try {
      const r = (await api.post("/clra-submissions/read", fd)).data || {};
      setAi(r);
      setF((x) => ({ ...x, ...Object.fromEntries(["expiry_date", "issued_on", "license_number"].filter((k) => r[k]?.value).map((k) => [k, r[k].value])) }));
      if (Object.keys(r).length) toast.success("Licence read. Check the details before sending.");
      else toast.warning("Couldn't read the details. Enter them by hand.");
    } catch (er) { toast.error(errMsg(er)); }
    setReading(false);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.file) return toast.error("Attach the renewed licence copy");
    if (!["application/pdf", "image/png", "image/jpeg"].includes(f.file.type)) return toast.error("Only PDF, JPG or PNG files are allowed");
    if (f.file.size > MAX_MB * 1024 * 1024) return toast.error(`File is too large (max ${MAX_MB} MB)`);
    const fd = new FormData();
    ["expiry_date", "issued_on", "license_number", "fee"].forEach((k) => f[k] && fd.append(k, f[k]));
    if (ai) fd.append("ai_read", JSON.stringify(ai));
    fd.append("file", f.file);
    setBusy(true);
    try { await api.post("/clra-submissions", fd); toast.success("Sent for approval"); setF({}); setAi(null); e.target.reset(); onDone(); } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  const conf = (k) => ai?.[k] && <span className={`ml-1 text-[10px] ${ai[k].confidence >= 80 ? "text-emerald-400" : "text-amber-400"}`} data-testid={`clra-ai-conf-${k}`}>AI {Math.round(ai[k].confidence)}%</span>;
  const field = (k, l, t, req) => (
    <label key={k} className="grid gap-1.5"><span className="text-xs text-slate-400">{l}{req && " *"}{conf(k)}</span>
      {t === "date" ? <DatePicker value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} required={req} fromYear={2000} testid={`clra-submit-${k}`} /> : <input type={t} required={req} min={t === "number" ? 0 : undefined} step={t === "number" ? "0.01" : undefined} className="c-input" value={f[k] || ""}
        onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`clra-submit-${k}`} />}</label>);
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
      <label className="grid gap-1.5 sm:col-span-2 lg:col-span-4"><span className="text-xs text-slate-400">1. Renewed copy (PDF/JPG/PNG, max 20 MB) *. The details are read automatically.</span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => pick(e.target.files[0])} className="text-sm" data-testid="clra-submit-file" /></label>
      {reading && <p className="sm:col-span-2 lg:col-span-4 text-xs text-[#00E599] flex items-center gap-1.5" data-testid="clra-ai-reading"><Sparkles size={13} className="animate-pulse" />Reading the licence…</p>}
      {ai && Object.keys(ai).length > 0 && <p className="sm:col-span-2 lg:col-span-4 text-xs text-amber-200 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2" data-testid="clra-ai-note">
        <Sparkles size={13} className="inline mr-1.5" />2. Check the details read from the licence{ai.max_workers?.value ? ` (max workers ${ai.max_workers.value})` : ""}. Correct anything that's wrong, then send.</p>}
      {field("expiry_date", "New expiry date", "date", true)}{field("issued_on", "Issued on", "date")}
      {field("license_number", "Licence number (if changed)", "text")}{field("fee", "Renewal fee paid (₹)", "number")}
      <Button type="submit" data-testid="clra-submit-btn" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold sm:col-span-2 lg:col-span-4 lg:justify-self-start" disabled={busy || reading}>
        <Upload size={14} className="mr-1.5" />Send for approval</Button>
    </form>);
};

const Row = ({ s, api, actions }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[#1F2A3E] bg-[#0B1017] px-3 py-2 text-sm" data-testid="clra-submission-row">
    <span className="min-w-0">{s.vendor_name} · new expiry <span className="font-mono">{s.expiry_date}</span>{s.fee ? <> · fee <span className="font-mono">{inr(s.fee)}</span></> : null}
      <span className="block text-[11px] text-slate-500">By {s.submitted_by} on {s.submitted_at?.slice(0, 10)}{s.reason && ` · Reason: ${s.reason}`}</span>
      {s.review_note && <span className="block text-[11px] text-emerald-300/90 mt-0.5" data-testid="clra-submission-note">Note from {s.reviewed_by}: {s.review_note}</span>}
      {s.release_hold_id && <span className="block text-[11px] text-amber-300 mt-0.5" data-testid="clra-submission-hold"><Lock size={11} className="inline mr-1" />
        {s.status === "APPROVED" ? "Payment hold released on approval" : "Also releases the CLRA payment hold. Needs principal employer approval"}</span>}
      {s.ai_differs?.length > 0 && <span className="block text-[11px] text-rose-300 mt-0.5" data-testid="clra-submission-ai-differs">
        Differs from what AI read: {s.ai_differs.map((k) => `${k.replace("_", " ")} (AI: ${s.ai_read?.[k]?.value})`).join(", ")}</span>}</span>
    <span className="flex items-center gap-1"><Pill tone={TONE[s.status]} testid="clra-submission-status">{s.status}</Pill>
      <button className="p-1.5 text-slate-400 hover:text-white" title="View copy" onClick={() => viewCopy(api, s.id)} data-testid="clra-submission-view"><Eye size={14} /></button>{actions}</span>
  </div>);

export const ClraPortal = ({ onSubmitted }) => {
  const api = useApi();
  const { me } = useMember();
  const { data: vendors, reload: reloadV } = useFetch("/vendors");
  const { data: subs, reload } = useFetch("/clra-submissions");
  const v = (vendors || []).find((x) => x.id === me?.vendor_id);
  return (
    <div className="c-surface p-6 c-rise mt-6 scroll-mt-24" id="clra-portal" data-testid="clra-portal-card">
      <div className="c-eyebrow mb-1">My CLRA licence</div>
      <p className="text-sm text-slate-400 mb-4" data-testid="clra-portal-current">{v?.clra_license_number || "No number on file"} · expires <span className="font-mono text-white">{v?.clra_license_expiry || "—"}</span>.
        Upload the renewed copy and the company will approve it.</p>
      <SubmitForm onDone={() => { reload(); reloadV(); onSubmitted?.(); }} />
      <div className="grid gap-2 mt-5" data-testid="clra-portal-history">{(subs || []).map((s) => <Row key={s.id} s={s} api={api} />)}</div>
    </div>);
};

export const ClraApprovals = ({ onChange }) => {
  const api = useApi();
  const { me } = useMember();
  const { data, reload } = useFetch("/clra-submissions?status=PENDING");
  const [rejecting, setRejecting] = useState(null);
  const [approving, setApproving] = useState(null);
  const gate = useScoreGate();
  if (!data?.length) return null;
  const approve = async (f) => {
    const s = approving;
    const ok = await gate.run((r) => api.post(`/clra-submissions/${s.id}/approve`, { note: f.note || null, score_override_reason: r }));
    if (ok) { toast.success(s.release_hold_id ? `CLRA licence renewed and payment released for ${s.vendor_name}` : `CLRA licence renewed for ${s.vendor_name}`); setApproving(null); reload(); onChange?.(); }
    return ok;
  };
  const reject = async (reason) => {
    try { await api.post(`/clra-submissions/${rejecting.id}/reject`, { reason }); toast.success("Renewal rejected"); setRejecting(null); reload(); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  return (
    <div className="c-surface p-6 c-rise mt-6 !border-amber-500/30" data-testid="clra-approvals-card">
      <div className="c-eyebrow mb-3 !text-amber-300">CLRA renewals awaiting approval · {data.length}</div>
      <div className="grid gap-2">{data.map((s) => <Row key={s.id} s={s} api={api} actions={<>
        {s.release_hold_id && me?.role !== "employer"
          ? <span className="text-[11px] text-slate-500 px-1" data-testid="clra-approve-pe-only">PE reviews</span>
          : <><button className="p-1.5 text-emerald-400 hover:text-emerald-300" title={s.release_hold_id ? "Approve and release payment" : "Approve"} onClick={() => setApproving(s)} data-testid="clra-approve-btn"><Check size={15} /></button>
            <button className="p-1.5 text-rose-400 hover:text-rose-300" title="Reject" onClick={() => setRejecting(s)} data-testid="clra-reject-btn"><X size={15} /></button></>}</>} />)}</div>
      {rejecting && <ReasonDialog title={`Reject renewal: ${rejecting.vendor_name}`} onClose={() => setRejecting(null)} onSubmit={reject} testid="clra-reject" />}
      {approving && <FormDialog open onOpenChange={() => setApproving(null)} title={`Approve renewal: ${approving.vendor_name}`} testid="clra-approve-form" initial={{}}
        fields={[{ name: "note", label: "Note for the contractor (optional), e.g. what was checked", maxLength: 500, placeholder: "Copy matches licence register; dates verified" }]} onSubmit={approve} />}
      {gate.dialog}

    </div>);
};

export const ClraBanner = () => {
  const { me } = useMember();
  const { data: vendors } = useFetch("/vendors");
  const { data: subs } = useFetch("/clra-submissions?status=PENDING");
  const v = (vendors || []).find((x) => x.id === me?.vendor_id);
  if (!v?.clra_license_expiry) return null;
  const n = new Date(), today = new Date(n.getFullYear(), n.getMonth(), n.getDate());
  const left = Math.round((new Date(`${v.clra_license_expiry}T00:00:00`) - today) / 86400000);
  if (left > 60) return null;
  const sent = subs?.length > 0;
  return (
    <a href="#clra-portal" data-testid="clra-reminder-banner"
      className={`c-surface c-rise mb-6 flex flex-wrap items-center justify-between gap-3 p-4 ${sent ? "!border-emerald-500/30" : left < 0 ? "!border-rose-500/50" : "!border-amber-500/40"}`}>
      <span className={`text-sm ${sent ? "text-emerald-300" : left < 0 ? "text-rose-300" : "text-amber-300"}`} data-testid="clra-reminder-text">
        <AlertTriangle size={15} className="inline mr-2" />
        {sent ? `Your CLRA renewal has been sent and is waiting for approval (expires ${v.clra_license_expiry}).`
          : left <= -15 ? `Your CLRA licence expired ${-left} day(s) ago on ${v.clra_license_expiry} and your payments are on hold. Upload the renewed copy to ask your principal employer to release them.`
          : left < 0 ? `Your CLRA licence expired ${-left} day(s) ago on ${v.clra_license_expiry}. Payments will be held in ${15 + left} day(s). Upload the renewed copy now.`
            : `Your CLRA licence expires in ${left} day(s) on ${v.clra_license_expiry}. Renew it and upload the copy.`}</span>
      {!sent && <span className="text-xs text-slate-300 underline">Upload renewal</span>}
    </a>);
};
